function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamAddToCartButton <iam-add-to-cart-button>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// Manage the state and behavior of the "Add to Cart" button in response to
// product form interactions.
// - Dynamically updates the button's text, availability, and loading states
//   based on product variant selection, quantity changes, form validation, and
//   the add-to-cart process.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:variant:changed`    → Updates the button state when the variant changes
// - `productForm:quantity:changed`   → Updates the button state when the quantity changes
// - `productForm:validation:changed` → Disables the button if form validation fails,
//                                      regardless of variant availability
// - `productForm:submitting`         → Updates the button state to "Adding to Cart"
//                                      while the request is being processed
// - `productForm:submitted:success`  → Temporarily updates the button state to
//                                      "Added to Cart" after an item is successfully
//                                      added to the cart
// - `productForm:submitted:error`    → Updates the button state to an "Error" state
//                                      if the add-to-cart request fails. If the
//                                      error is due to the item being sold out,
//                                      the button state is set to "Sold Out", otherwise
//                                      it defaults to a generic error state.
// ----------------------------------------------------------------------------
// Gotchas:
// - Form validation can override button availability, keeping it disabled even
//   if a variant is available.
// - After a successful add-to-cart event, the button remains in the "Item Added"
//   state for ITEM_ADDED_DURATION (default 3 secs) before resetting.
// - If an unknown button state occurs, it defaults to a generic error state and
//   disables the button (until the variant selection changes).
// - Translation strings are defined in the `products.add_to_cart` namespace of
//   the locales file.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import iamFormElement from 'scripts/classes/iamFormElement';
import Debug from 'scripts/libs/debug';

// -------------------------------------
// Web Component
// -------------------------------------

class iamAddToCartButton extends iamFormElement {
  getControls() {
    var submit = this.querySelector('button[type="submit"], button[name="add"]');
    return submit ? [submit] : [];
  }

  /**
   * Initialize the component when the product form is initialized
   */
  onFormInit() {
    // Bind event listeners
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:quantity:changed', this.onQuantityChange.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:property:changed', this.onPropertyChange.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:price:updated', this.onPriceUpdated.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:validation:changed', this.onValidate.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:submitting', this.onAdding.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:submitted:success', this.onAddSuccess.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });
    iamotaEventHandler.subscribe('productForm:submitted:error', this.onAddError.bind(this), {
      context: this.iamForm,
      source: "add-to-cart-button.js"
    });

    // State
    this.state = iamAddToCartButton.BUTTON_STATES.LOADING;
    this.initializeButtonState();
  }
  disconnectedCallback() {
    var _this$priceObserver;
    (_this$priceObserver = this.priceObserver) === null || _this$priceObserver === void 0 || _this$priceObserver.disconnect();
    clearTimeout(this.priceRefreshTimeout);
    clearTimeout(this.awaitPriceChangeTimeout);
    super.disconnectedCallback();
  }
  onValidate(_ref) {
    var {
      isValid
    } = _ref;
    if (isValid) {
      // Validation Passes -> refresh to match the current variant's state
      this.refreshState();
    } else {
      // Validation Fails -> disable the add-to-cart button
      this.setDisabled(true);
    }
  }
  onVariantChange(_ref2) {
    var {
      variant
    } = _ref2;
    this.refreshState(variant);
  }
  onQuantityChange() {
    // If the qty changes, trigger a variant refresh
    // e.g. in case the copy includes the qty in the price
    this.refreshState();
  }
  onPropertyChange() {
    this.schedulePriceRefresh('propertyChanged');
    this.awaitDisplayedPriceChange();
  }
  onPriceUpdated() {
    this.schedulePriceRefresh('priceUpdated');
    this.awaitDisplayedPriceChange();
  }
  getDebugControlData() {
    var _this$control, _this$control2, _this$control3;
    return {
      controlId: ((_this$control = this.control) === null || _this$control === void 0 ? void 0 : _this$control.id) || null,
      controlClassName: ((_this$control2 = this.control) === null || _this$control2 === void 0 ? void 0 : _this$control2.className) || null,
      controlText: ((_this$control3 = this.control) === null || _this$control3 === void 0 ? void 0 : _this$control3.innerHTML) || null,
      isConnected: this.isConnected,
      isHidden: this.offsetParent === null,
      inStickyContainer: Boolean(this.closest('#productStickyContainer'))
    };
  }

  /**
   * Handle form submission
   * @param {event} event
   */
  onAdding() {
    this.setState(iamAddToCartButton.BUTTON_STATES.ADDING_TO_CART);
  }
  onAddSuccess() {
    this.setState(iamAddToCartButton.BUTTON_STATES.ITEM_ADDED);
  }
  onAddError(_ref3) {
    var _error$description;
    var {
      error
    } = _ref3;
    if ((_error$description = error.description) !== null && _error$description !== void 0 && _error$description.includes('sold out')) {
      this.setState(iamAddToCartButton.BUTTON_STATES.SOLD_OUT);
    } else {
      this.setState(iamAddToCartButton.BUTTON_STATES.ERROR);
    }
  }

  /**
   * Refresh the button state based on the currently selected variant
   * @param {*} variant
   */
  refreshState() {
    var _this$iamForm$getCurr, _this$iamForm, _this$iamForm$getCurr2, _this$iamForm2, _this$iamForm$getCurr3, _this$iamForm3;
    var variant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.iamForm.getCurrentVariant();
    Debug.verbose('[add-to-cart-button] refreshState', {
      variantId: variant === null || variant === void 0 ? void 0 : variant.id,
      variantAvailable: variant === null || variant === void 0 ? void 0 : variant.available,
      displayedPrice: (_this$iamForm$getCurr = (_this$iamForm = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr === void 0 ? void 0 : _this$iamForm$getCurr.call(_this$iamForm),
      quantity: (_this$iamForm$getCurr2 = (_this$iamForm2 = this.iamForm).getCurrentQuantity) === null || _this$iamForm$getCurr2 === void 0 ? void 0 : _this$iamForm$getCurr2.call(_this$iamForm2),
      properties: (_this$iamForm$getCurr3 = (_this$iamForm3 = this.iamForm).getCurrentProperties) === null || _this$iamForm$getCurr3 === void 0 ? void 0 : _this$iamForm$getCurr3.call(_this$iamForm3)
    });
    if (!variant) {
      this.setState(iamAddToCartButton.BUTTON_STATES.UNAVAILABLE, variant);
    } else if (variant.available) {
      this.setState(iamAddToCartButton.BUTTON_STATES.AVAILABLE, variant);
    } else {
      this.setState(iamAddToCartButton.BUTTON_STATES.SOLD_OUT, variant);
    }
  }

  /**
   * Set the current state and update the UI accordingly
   */
  setState(newState) {
    var _currentData$variant, _currentData$variant2, _this$iamForm$getCurr4, _this$iamForm4;
    this.state = newState;
    var currentData = this.getCurrentTranslationData();
    Debug.verbose('[add-to-cart-button] setState', _objectSpread({
      state: this.state,
      variantId: (_currentData$variant = currentData.variant) === null || _currentData$variant === void 0 ? void 0 : _currentData$variant.id,
      variantPrice: (_currentData$variant2 = currentData.variant) === null || _currentData$variant2 === void 0 ? void 0 : _currentData$variant2.price,
      quantity: currentData.quantity,
      displayedPrice: (_this$iamForm$getCurr4 = (_this$iamForm4 = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr4 === void 0 ? void 0 : _this$iamForm$getCurr4.call(_this$iamForm4)
    }, this.getDebugControlData()));
    switch (this.state) {
      case iamAddToCartButton.BUTTON_STATES.UNAVAILABLE:
        // (i.e. this combination of variation options likely doesn't exist)
        this.updateButton({
          buttonText: translate('Unavailable', 'addToCart.unavailable', _objectSpread({}, currentData)),
          isDisabled: true
        });
        break;
      case iamAddToCartButton.BUTTON_STATES.AVAILABLE:
        this.updateButton({
          buttonText: translate('Add to Cart - {{ formatMoney (multiply variant.price quantity) }}', 'addToCart.available', _objectSpread({}, currentData)),
          isDisabled: false
        });
        break;
      case iamAddToCartButton.BUTTON_STATES.SOLD_OUT:
        // (i.e. likely sold out or discontinued)
        this.updateButton({
          buttonText: translate('Sold Out - {{ formatMoney (multiply variant.price quantity) }}', 'addToCart.sold_out', _objectSpread({}, currentData)),
          isDisabled: true
        });
        break;
      case iamAddToCartButton.BUTTON_STATES.LOADING:
        this.updateButton({
          buttonText: translate('Loading...', 'addToCart.loading', _objectSpread({}, currentData)),
          isDisabled: true,
          isLoading: true
        });
        break;
      case iamAddToCartButton.BUTTON_STATES.ADDING_TO_CART:
        this.updateButton({
          buttonText: translate('Adding to cart...', 'addToCart.adding_to_cart', _objectSpread({}, currentData)),
          isDisabled: true,
          isLoading: true
        });
        break;
      case iamAddToCartButton.BUTTON_STATES.ITEM_ADDED:
        this.updateButton({
          buttonText: translate('Item added to cart', 'addToCart.item_added', _objectSpread({}, currentData)),
          isDisabled: true
        });
        setTimeout(() => {
          this.refreshState();
        }, iamAddToCartButton.ITEM_ADDED_DURATION);
        break;
      case iamAddToCartButton.BUTTON_STATES.ERROR:
        this.updateButton({
          buttonText: translate('Unable to add', 'addToCart.error', _objectSpread({}, currentData)),
          isDisabled: true
        });
        break;
      default:
        console.warn("Unknown add-to-cart-button state: ".concat(this.state));
        this.setState(iamAddToCartButton.BUTTON_STATES.ERROR);
        break;
    }
  }

  /**
   * Update button UI based on the provided options
   */
  updateButton(_ref4) {
    var {
      buttonText = this.control.innerHTML,
      isDisabled = false,
      isLoading = false
    } = _ref4;
    this.refreshControls();
    if (!this.control) return;
    Debug.details('[add-to-cart-button] updateButton', _objectSpread({
      requestedButtonText: buttonText,
      currentButtonText: this.control.innerHTML,
      isDisabled,
      isLoading
    }, this.getDebugControlData()));
    this.setText(buttonText);
    this.setLoading(isLoading);
    this.setDisabled(isDisabled, 'updateButton');
  }

  /**
   * Set the button text
   * @param {string} label
  */
  setText(buttonText) {
    var _this$iamForm$getCurr5, _this$iamForm5, _this$iamForm$getCurr6, _this$iamForm6;
    this.refreshControls();
    if (!this.control) return;
    buttonText = iamotaEventHandler.applyFiltersSync('productForm:addToCartButton:buttonText', buttonText, {}, {
      context: this.iamForm
    });
    Debug.details('[add-to-cart-button] setText', _objectSpread({
      previousButtonText: this.control.innerHTML,
      buttonText,
      variantId: (_this$iamForm$getCurr5 = (_this$iamForm5 = this.iamForm).getCurrentVariant) === null || _this$iamForm$getCurr5 === void 0 || (_this$iamForm$getCurr5 = _this$iamForm$getCurr5.call(_this$iamForm5)) === null || _this$iamForm$getCurr5 === void 0 ? void 0 : _this$iamForm$getCurr5.id,
      displayedPrice: (_this$iamForm$getCurr6 = (_this$iamForm6 = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr6 === void 0 ? void 0 : _this$iamForm$getCurr6.call(_this$iamForm6)
    }, this.getDebugControlData()));
    this.control.innerHTML = buttonText;
    this.control.setAttribute('aria-label', buttonText);
    Debug.details('[add-to-cart-button] setText applied', _objectSpread({
      appliedButtonText: this.control.innerHTML,
      ariaLabel: this.control.getAttribute('aria-label')
    }, this.getDebugControlData()));
  }
  getCurrentTranslationData() {
    var _this$iamForm$getCurr7, _this$iamForm7, _currentData$variant3, _currentData$variant4;
    var currentData = this.iamForm.getCurrentData();
    var displayedPrice = (_this$iamForm$getCurr7 = (_this$iamForm7 = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr7 === void 0 ? void 0 : _this$iamForm$getCurr7.call(_this$iamForm7);
    if (currentData.variant && Number.isFinite(displayedPrice)) {
      currentData.variant = _objectSpread(_objectSpread({}, currentData.variant), {}, {
        price: displayedPrice
      });
    }
    Debug.details('[add-to-cart-button] translationData', _objectSpread({
      variantId: (_currentData$variant3 = currentData.variant) === null || _currentData$variant3 === void 0 ? void 0 : _currentData$variant3.id,
      variantPrice: (_currentData$variant4 = currentData.variant) === null || _currentData$variant4 === void 0 ? void 0 : _currentData$variant4.price,
      displayedPrice,
      quantity: currentData.quantity,
      properties: currentData.properties
    }, this.getDebugControlData()));
    return currentData;
  }
  observePriceChanges() {
    var _this$priceObserver2;
    var priceContainer = this.iamForm;
    (_this$priceObserver2 = this.priceObserver) === null || _this$priceObserver2 === void 0 || _this$priceObserver2.disconnect();
    this.priceObserver = new MutationObserver(mutations => {
      if (!this.hasPriceMutation(mutations)) return;
      Debug.verbose('[add-to-cart-button] price mutation observed', {
        mutations
      });
      this.schedulePriceRefresh('mutation');
    });
    this.priceObserver.observe(priceContainer, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true
    });
  }
  initializeButtonState() {
    this.refreshControls();
    if (!this.control) {
      Debug.verbose('[add-to-cart-button] waiting for inner control');
      requestAnimationFrame(() => {
        this.initializeButtonState();
      });
      return;
    }
    Debug.verbose('[add-to-cart-button] initialized', {
      control: this.control
    });
    this.observePriceChanges();
    this.refreshState();
  }
  hasPriceMutation() {
    var mutations = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    return mutations.some(mutation => {
      var _mutation$target, _targetElement$closes;
      var targetElement = mutation.target instanceof Element ? mutation.target : (_mutation$target = mutation.target) === null || _mutation$target === void 0 ? void 0 : _mutation$target.parentElement;
      if (targetElement !== null && targetElement !== void 0 && (_targetElement$closes = targetElement.closest) !== null && _targetElement$closes !== void 0 && _targetElement$closes.call(targetElement, '#ProductPrice, [data-product-pricing], iam-product-price')) {
        return true;
      }
      return Array.from(mutation.addedNodes || []).some(node => {
        var _node$matches, _node$querySelector;
        return node instanceof Element && (((_node$matches = node.matches) === null || _node$matches === void 0 ? void 0 : _node$matches.call(node, '#ProductPrice, [data-product-pricing], iam-product-price')) || ((_node$querySelector = node.querySelector) === null || _node$querySelector === void 0 ? void 0 : _node$querySelector.call(node, '#ProductPrice, [data-product-pricing], iam-product-price')));
      });
    });
  }
  schedulePriceRefresh() {
    var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'unknown';
    Debug.verbose('[add-to-cart-button] schedulePriceRefresh', {
      source
    });
    requestAnimationFrame(() => {
      this.refreshState();
    });
    clearTimeout(this.priceRefreshTimeout);
    this.priceRefreshTimeout = setTimeout(() => {
      this.refreshState();
    }, 60);
  }
  awaitDisplayedPriceChange() {
    var _this$iamForm$getCurr8, _this$iamForm8, _this$iamForm$getCurr9, _this$iamForm9;
    var previousPrice = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : (_this$iamForm$getCurr8 = (_this$iamForm8 = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr8 === void 0 ? void 0 : _this$iamForm$getCurr8.call(_this$iamForm8);
    var attempt = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    clearTimeout(this.awaitPriceChangeTimeout);
    var currentPrice = (_this$iamForm$getCurr9 = (_this$iamForm9 = this.iamForm).getCurrentDisplayPrice) === null || _this$iamForm$getCurr9 === void 0 ? void 0 : _this$iamForm$getCurr9.call(_this$iamForm9);
    if (attempt > 12 || Number.isFinite(previousPrice) && Number.isFinite(currentPrice) && currentPrice !== previousPrice) {
      Debug.verbose('[add-to-cart-button] awaitDisplayedPriceChange resolved', _objectSpread({
        previousPrice,
        currentPrice,
        attempt
      }, this.getDebugControlData()));
      this.refreshState();
      return;
    }
    Debug.details('[add-to-cart-button] awaitDisplayedPriceChange pending', _objectSpread({
      previousPrice,
      currentPrice,
      attempt
    }, this.getDebugControlData()));
    this.awaitPriceChangeTimeout = setTimeout(() => {
      this.awaitDisplayedPriceChange(previousPrice, attempt + 1);
    }, 75);
  }

  /**
   * Disable the button
   * @param {boolean} isDisabled
   */
  setDisabled(isDisabled) {
    var _this$iamForm$validat;
    // Force disabled state if the form is invalid
    var isFormValid = (_this$iamForm$validat = this.iamForm.validator) === null || _this$iamForm$validat === void 0 ? void 0 : _this$iamForm$validat.isFormValid();
    var newState = isDisabled || !isFormValid;
    super.setDisabled(newState);
  }
}
_defineProperty(iamAddToCartButton, "ITEM_ADDED_DURATION", 3000);
// Duration to show "Item added" message (ms) before reverting to default state
_defineProperty(iamAddToCartButton, "BUTTON_STATES", {
  AVAILABLE: 'available',
  UNAVAILABLE: 'unavailable',
  SOLD_OUT: 'sold_out',
  ITEM_ADDED: 'item_added',
  LOADING: 'loading',
  ADDING_TO_CART: 'adding_to_cart',
  ERROR_MAX_QUANTITY: 'error_max_quantity',
  ERROR: 'error_unknown'
});
customElements.define('iam-add-to-cart-button', iamAddToCartButton);